import { useState } from "react";

export interface CameraFeedData {
  id: number;
  label: string;
  status: "idle" | "pass" | "fail";
  barcode: string;
  lastScanTime: string | null;
}

interface UseCameraFeedsOptions {
  initialLabels?: string[];
}

interface UseCameraFeedsResult {
  cameraFeeds: CameraFeedData[];
  updateCameraFeed: (index: number, data: Partial<CameraFeedData>) => void;
  updateCameraStatus: (
    index: number,
    barcode: string,
    isValid: boolean
  ) => void;
  clearCameraStatus: (index: number) => void;
  resetCameraFeeds: () => void;
}

export const useCameraFeeds = (
  options: UseCameraFeedsOptions = {}
): UseCameraFeedsResult => {
  const { initialLabels = ["Camera One", "Camera Two", "Camera Three"] } =
    options;

  // Initialize camera feeds
  const initialCameraFeeds: CameraFeedData[] = initialLabels.map(
    (label, index) => ({
      id: index + 1,
      label,
      status: "idle",
      barcode: "",
      lastScanTime: null,
    })
  );

  const [cameraFeeds, setCameraFeeds] =
    useState<CameraFeedData[]>(initialCameraFeeds);

  // Update a specific camera feed with partial data
  const updateCameraFeed = (index: number, data: Partial<CameraFeedData>) => {
    setCameraFeeds((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }

      const updated = [...prev];
      updated[index] = { ...updated[index], ...data };
      return updated;
    });
  };

  // Update camera status based on barcode scan
  const updateCameraStatus = (
    index: number,
    barcode: string,
    isValid: boolean
  ) => {
    setCameraFeeds((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }

      const updated = [...prev];

      // Only update if this is a new barcode for this camera
      if (updated[index].barcode === barcode) {
        return prev;
      }

      updated[index] = {
        ...updated[index],
        status: isValid ? "pass" : "fail",
        barcode: barcode,
        lastScanTime: new Date().toLocaleTimeString(),
      };

      return updated;
    });
  };

  // Clear camera status when barcode is no longer detected
  const clearCameraStatus = (index: number) => {
    setCameraFeeds((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }

      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        barcode: "",
        status: "idle",
        lastScanTime: null,
      };

      return updated;
    });
  };

  // Reset all camera feeds to initial state
  const resetCameraFeeds = () => {
    setCameraFeeds(initialCameraFeeds);
  };

  return {
    cameraFeeds,
    updateCameraFeed,
    updateCameraStatus,
    clearCameraStatus,
    resetCameraFeeds,
  };
};
